import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// The dialog title is the h2, so the headings of the text start at h3.
const components: Components = {
  h1: ({ children }) => (
    <h3 className="mb-4 mt-10 text-xl font-semibold tracking-tight text-zinc-900 first:mt-0">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h3 className="mb-3 mt-10 text-lg font-semibold text-zinc-900 first:mt-0">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="mb-3 mt-8 text-base font-semibold text-zinc-900 first:mt-0">{children}</h4>
  ),
  h4: ({ children }) => (
    <h5 className="mb-2 mt-6 text-sm font-semibold text-zinc-900 first:mt-0">{children}</h5>
  ),
  h5: ({ children }) => (
    <h6 className="mb-2 mt-5 text-sm font-semibold text-zinc-800 first:mt-0">{children}</h6>
  ),
  h6: ({ children }) => (
    <h6 className="mb-2 mt-5 text-sm font-medium text-zinc-700 first:mt-0">{children}</h6>
  ),
  p: ({ children }) => <p className="my-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="my-3 list-disc space-y-1.5 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 list-decimal space-y-1.5 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-zinc-900 underline underline-offset-2 hover:text-zinc-600"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-2 border-zinc-300 pl-4 text-zinc-600">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-8 border-zinc-200" />,
  code: ({ children }) => (
    <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs text-zinc-800">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="my-4 overflow-x-auto rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs leading-5 [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-left text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-zinc-300 px-2 py-1.5 font-semibold text-zinc-800">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-zinc-200 px-2 py-1.5 align-top">{children}</td>
  ),
};

/** Instructions written by the team, rendered without raw HTML. */
export const WorkflowMarkdown = ({ children }: { children: string }) => (
  <div className="text-sm text-zinc-700">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  </div>
);
